import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { StatsDays, WsServerMessage } from "@agentdash/shared";
import { api, type RunsQueryOpts } from "../lib/api-client.js";
import { connectLive, type WsConnectionState } from "../lib/ws-client.js";
import { LIVE_RUNNING_RUNS, queryKeys } from "./keys.js";

export function useRuns(opts?: RunsQueryOpts) {
  return useQuery({
    queryKey: queryKeys.runs(opts),
    queryFn: () => api.listRuns(opts),
    refetchInterval: 10_000,
    // Keep the current page visible while the next/prev page loads (no flicker).
    placeholderData: keepPreviousData,
  });
}

/** The Live view's running set — the query the snapshot frame seeds. */
export function useLiveRunningRuns() {
  return useRuns(LIVE_RUNNING_RUNS);
}

export function useRunDetail(traceId: string) {
  return useQuery({
    queryKey: queryKeys.run(traceId),
    queryFn: () => api.getRun(traceId),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.status === "running") return 3_000;
      // Poll briefly while a summary is being generated — LISTEN/NOTIFY will
      // normally beat this, but it covers a momentarily disconnected WS.
      if (data?.summary_status === "pending") return 3_000;
      return false;
    },
  });
}

export function useRegenerateSummary(traceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.summarizeRun(traceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.run(traceId) }),
  });
}

/**
 * Opens the live WS, invalidates the relevant queries when run-change arrives.
 * FR-3.2: client refetches; server does not push full payloads.
 *
 * Pass `enabled=false` to skip connecting (e.g. while unauthenticated — the
 * auth gate would 401 the upgrade anyway and we'd thrash on reconnect).
 */
export function useLiveSync(enabled: boolean = true): WsConnectionState {
  const qc = useQueryClient();
  const [state, setState] = useState<WsConnectionState>("connecting");

  useEffect(() => {
    if (!enabled) {
      setState("closed");
      return;
    }
    const handle = connectLive({
      onStateChange: setState,
      onMessage: (msg: WsServerMessage) => {
        if (msg.type === "run-change") {
          void qc.invalidateQueries({ queryKey: ["runs"] });
          void qc.invalidateQueries({ queryKey: queryKeys.run(msg.trace_id) });
        } else if (msg.type === "snapshot") {
          // FR-3.1: the server sends the running set on connect so the Live
          // view can paint immediately — most valuable on *re*connect, where
          // the rendered list may be minutes stale. It is written under
          // LIVE_RUNNING_RUNS, the same opts useLiveRunningRuns subscribes
          // with, so the two keys cannot drift apart. A running-runs query
          // built from any other opts (a pager, its own `limit`) is not seeded
          // and relies on the invalidation below.
          qc.setQueryData(queryKeys.runs(LIVE_RUNNING_RUNS), msg.runs);
          // Still invalidate the whole family: the other `runs` queries (the
          // /runs pager, "Recently completed") went stale while we were
          // disconnected, and refetching the running one reconciles it against
          // anything that changed between the snapshot and this handler. An
          // open run detail went stale the same way, so it refetches too.
          void qc.invalidateQueries({ queryKey: ["runs"] });
          void qc.invalidateQueries({ queryKey: ["run"] });
        }
      },
    });
    return () => handle.close();
  }, [qc, enabled]);

  return state;
}

export function useStats(days: StatsDays) {
  return useQuery({
    queryKey: queryKeys.stats(days),
    queryFn: () => api.getStats(days),
    staleTime: 60_000,
  });
}
